import { RydLoadProjectDialog } from "../components/dialogs/RydLoadProjectDialog";
import {
    IntervalPattern,
    ScalePattern,
    ScalePatternId
} from "../model/scales";

type HardcodedScalePatternDefinition = {
    id: ScalePatternId,
    name?: string,
    intervals: number[]
}

const SCALE_PATTERNS: HardcodedScalePatternDefinition[] = [
    {
        /**
         * A diatonic scale is a heptatonic (seven-note) scale that includes five whole steps
         * (whole tones) and two half steps (semitones) in each octave, in which the two half
         * steps are separated from each other by either two or three whole steps.
         * https://en.wikipedia.org/wiki/Diatonic_scale
         */
        id: 'diatonic',
        name: 'Diatonic',
        intervals: [2, 2, 1, 2, 2, 2, 1]
    },
    // {
    //     id: 'minor',
    //     intervals: [2, 1, 2, 2, 1, 2, 2]
    // },
    {
        id: 'natural_minor',
        name: 'Natural Minor',
        intervals: [2, 1, 2, 2, 1, 2, 2]
    },
    {
        id: 'harmonic_minor',
        name: 'Harmonic Minor',
        intervals: [2, 1, 2, 2, 1, 3, 1]
    },
    {
        id: 'melodic_minor',
        name: 'Melodic Minor',
        intervals: [2, 1, 2, 2, 2, 2, 1]
    },
    {
        id: 'whole_tone',
        name: 'Whole Tone',
        intervals: [2, 2, 2, 2, 2, 2]
    },
    {
        id: 'byzantine',
        name: 'Byzantine',
        intervals: [1, 3, 1, 2, 1, 3, 1]
    },
    {
        id: 'chromatic',
        name: 'Chromatic',
        intervals: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]
    },
    {
        id: 'pentatonic_major',
        name: 'Pentatonic Major',
        intervals: [2, 2, 3, 2, 3]
    },
    {
        id: 'pentatonic_minor',
        name: 'Pentatonic Minor',
        intervals: [3, 2, 2, 3, 2]
    },
    {
        id: 'blues',
        name: 'Blues',
        intervals: [3, 2, 1, 1, 3, 2]
    }
];

export namespace ScalesService {
    const scalePatterns = new Map<string, ScalePattern>();
    SCALE_PATTERNS.map(sp => new ScalePattern(new IntervalPattern(sp.id, sp.intervals), sp.id, sp['name']))
        .forEach(insert);

    export function insert(pattern: ScalePattern) {
        if (scalePatterns.has(pattern.id)) {
            throw new Error(`Duplicate scale pattern ID: ${pattern.id}`);
        }
        if (findByIntervalPattern(pattern.getIntervals())) {
            throw new Error(`Duplicate scale pattern intervals: ${pattern.getIntervals().join(",")}`);
        }
        scalePatterns.set(pattern.id, pattern);
    }

    export function findByIntervalPattern(intervals: number[]): ScalePattern | undefined {
        const r = Array.from(scalePatterns.values()).find(sp => {
            const spIntervals = sp.getIntervals();
            return arrayEquals(spIntervals, intervals);
        });
        if (r) return r;
        return undefined;
    }

    export function get(id: ScalePatternId): ScalePattern | undefined {
        return scalePatterns.get(id);
    }

    export function listPatterns(): ScalePattern[] {
        return Array.from(scalePatterns.values());
    }
}

function arrayEquals(a: any[], b: any[]): boolean {
    return a.length === b.length && a.every((val, index) => val === b[index]);
}